import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import { Box, Button, IconButton, LinearProgress, Snackbar, Stack, Typography } from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import TeacherGlobalHeader from '../component/TeacherGlobalHeader';
import { BLUE, BLUE_HOVER, BORDER, INTER, NAVY, PAGE_BACKGROUND, POPPINS, PROGRESS_TRACK, SLATE, SOFT_BLUE, SURFACE_TINT } from '../teacherStyles';

const classes = [
    {
        id: 'intermediate-mandarin-ii',
        name: 'Intermediate Mandarin II',
        schedule: 'Mon / Wed · 10:00 AM',
        students: 18,
        currentLesson: 'Ordering Food at a Restaurant',
        progress: 72,
        color: BLUE,
    },
    {
        id: 'mandarin-foundations',
        name: 'Mandarin Foundations',
        schedule: 'Tue / Thu · 1:30 PM',
        students: 24,
        currentLesson: 'Daily Routines and Time',
        progress: 46,
        color: NAVY,
    },
];

export default function TeacherDashboardPage() {
    const history = useHistory();
    const [message, setMessage] = React.useState<string | null>(null);

    const showPlanned = (feature: string) => setMessage(`${feature} will be a separate teacher page.`);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: PAGE_BACKGROUND }}>
            <TeacherGlobalHeader onNotice={setMessage} />

            <Box component="main" sx={{ width: '100%', maxWidth: 1180, mx: 'auto', p: { xs: 2, sm: 3, lg: 4 } }}>
                <Box>
                    <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 25, sm: 30 }, letterSpacing: '-0.025em', color: NAVY }}>
                        Good morning, Emily
                    </Typography>
                </Box>

                <Box sx={{ maxWidth: 900, mt: 3.5 }}>
                    <Box sx={{ backgroundColor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '20px', overflow: 'hidden' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ px: { xs: 2, sm: 2.75 }, py: 2.5, borderBottom: `1px solid ${BORDER}` }}>
                            <Box>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>Your classes</Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 12.5, color: SLATE, mt: 0.25 }}>2 active classes · 42 students</Typography>
                            </Box>
                            <Button startIcon={<AddRoundedIcon />} onClick={() => showPlanned('Create class')} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: BLUE, textTransform: 'none' }}>Create class</Button>
                        </Stack>

                        <Stack divider={<Box sx={{ height: `1px`, backgroundColor: BORDER }} />}>
                            {classes.map(item => (
                                <Box key={item.name} sx={{ p: { xs: 2, sm: 2.75 }, '&:hover': { backgroundColor: SURFACE_TINT } }}>
                                    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                                        <Box sx={{ width: 46, height: 46, flexShrink: 0, borderRadius: '14px', display: 'grid', placeItems: 'center', backgroundColor: `${item.color}12`, color: item.color }}>
                                            <GroupsRoundedIcon />
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 14.5, color: NAVY }}>{item.name}</Typography>
                                            <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE, mt: 0.25 }}>{item.students} students · {item.schedule}</Typography>
                                        </Box>
                                        <IconButton aria-label={`Open ${item.name}`} onClick={() => history.push(`/mockup/teacher/class/${item.id}/overview`)} sx={{ mt: -0.5, mr: -0.5, color: BLUE, backgroundColor: SOFT_BLUE, '&:hover': { backgroundColor: BLUE_HOVER } }}>
                                            <ChevronRightRoundedIcon />
                                        </IconButton>
                                    </Stack>

                                    <Box sx={{ ml: { xs: 0, sm: 7.75 }, mt: 2 }}>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.75}>
                                            <Box>
                                                <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11.5, color: SLATE, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Current lesson</Typography>
                                                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: NAVY, mt: 0.3 }}>{item.currentLesson}</Typography>
                                            </Box>
                                            <Typography sx={{ alignSelf: { sm: 'flex-end' }, fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: NAVY }}>{item.progress}% complete</Typography>
                                        </Stack>
                                        <LinearProgress variant="determinate" value={item.progress} sx={{ mt: 1, height: 6, borderRadius: 3, backgroundColor: PROGRESS_TRACK, '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: item.color } }} />
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Box>

            </Box>

            <Snackbar
                open={Boolean(message)}
                autoHideDuration={2600}
                onClose={() => setMessage(null)}
                message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                ContentProps={{ sx: { borderRadius: '12px', backgroundColor: NAVY, fontFamily: INTER, fontSize: 13 } }}
            />
        </Box>
    );
}
